import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AppBrandLogo } from './AppBrandLogo';
import { PWA_UPDATE_BANNER_VISIBILITY_EVENT } from '../../utils/pwaUpdateBannerEvents';
import {
  isIosSafariBrowserTab,
  isIosSafariInstallEligible,
  isMacSafariInstallEligible,
  isPwaListedAsInstalledRelatedApp,
  isRunningAsInstalledPwa,
  shouldShowNonChromeInstallHint,
  subscribeInstalledPwaDisplayMode,
} from '../../utils/pwaInstallEnvironment';
import {
  clearDeferredInstallPrompt,
  clearStalePwaInstallFlag,
  ensurePwaInstallOfferCapture,
  getDeferredInstallPrompt,
  markPwaInstalled,
  PWA_INSTALLED_KEY,
  PWA_IOS_DISMISSED_SESSION_KEY,
  subscribePwaInstallOffer,
  type BeforeInstallPromptEvent,
} from '../../utils/pwaInstallOffer';

interface PwaInstallBannerProps {
  className?: string;
}

const PROMPT_FALLBACK_DELAY_MS = 4000;
const ANIMATION_DURATION_MS = 280;

const isRunningAsInstalledApp = () => isRunningAsInstalledPwa();

const isIosSafariSessionDismissed = () => sessionStorage.getItem(PWA_IOS_DISMISSED_SESSION_KEY) === 'true';

const dismissIosSafariInstallForSession = () => {
  sessionStorage.setItem(PWA_IOS_DISMISSED_SESSION_KEY, 'true');
};

type ManualInstallKind = 'ios' | 'mac' | 'generic';

const PwaInstallBanner: React.FC<PwaInstallBannerProps> = ({ className = '' }) => {
  const { t } = useTranslation('common');
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [wantsInstall, setWantsInstall] = useState(false);
  const [isInstalling, setIsInstalling] = useState(false);
  const [manualKind, setManualKind] = useState<ManualInstallKind | null>(null);
  const [dismissedForSession, setDismissedForSession] = useState(
    () => isIosSafariBrowserTab() && isIosSafariSessionDismissed()
  );
  const [updateBannerVisible, setUpdateBannerVisible] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const [isAnimatingIn, setIsAnimatingIn] = useState(false);
  const deferredPromptRef = useRef<BeforeInstallPromptEvent | null>(null);

  const requestInstallBanner = useCallback(() => {
    if (dismissedForSession) return;
    setWantsInstall(true);
  }, [dismissedForSession]);

  useEffect(() => {
    deferredPromptRef.current = deferredPrompt;
  }, [deferredPrompt]);

  useEffect(() => {
    const onUpdateBanner = (e: Event) => {
      setUpdateBannerVisible(Boolean((e as CustomEvent<{ visible?: boolean }>).detail?.visible));
    };
    window.addEventListener(PWA_UPDATE_BANNER_VISIBILITY_EVENT, onUpdateBanner);
    return () => window.removeEventListener(PWA_UPDATE_BANNER_VISIBILITY_EVENT, onUpdateBanner);
  }, []);

  useEffect(() => {
    const hideBecauseInstalled = () => {
      markPwaInstalled();
      setDeferredPrompt(null);
      setManualKind(null);
      setWantsInstall(false);
    };

    const refreshInstalledShell = () => {
      if (isRunningAsInstalledApp()) hideBecauseInstalled();
    };

    refreshInstalledShell();
    const unsubscribeDisplayMode = subscribeInstalledPwaDisplayMode(refreshInstalledShell);
    const settleTimers = [0, 400, 1000].map((ms) => window.setTimeout(refreshInstalledShell, ms));
    let cancelled = false;
    void isPwaListedAsInstalledRelatedApp().then((relatedInstalled) => {
      if (cancelled || !relatedInstalled) return;
      hideBecauseInstalled();
    });

    const cleanupInstalledChecks = () => {
      cancelled = true;
      settleTimers.forEach((id) => window.clearTimeout(id));
      unsubscribeDisplayMode();
    };

    if (isRunningAsInstalledApp()) {
      return cleanupInstalledChecks;
    }

    ensurePwaInstallOfferCapture();
    clearStalePwaInstallFlag();

    const syncPrompt = () => {
      if (isRunningAsInstalledApp()) {
        setDeferredPrompt(null);
        setManualKind(null);
        setWantsInstall(false);
        return;
      }
      const prompt = getDeferredInstallPrompt();
      setDeferredPrompt(prompt);
      if (prompt && !dismissedForSession) {
        setManualKind(null);
        requestInstallBanner();
      }
    };
    syncPrompt();
    const unsubscribe = subscribePwaInstallOffer(syncPrompt);

    const handleAppInstalled = () => {
      if (isIosSafariBrowserTab()) {
        dismissIosSafariInstallForSession();
      } else {
        markPwaInstalled();
      }
      setDeferredPrompt(null);
      setWantsInstall(false);
    };

    window.addEventListener('appinstalled', handleAppInstalled);

    const installedByFlag = localStorage.getItem(PWA_INSTALLED_KEY) === 'true';
    const iosSessionDismissed = isIosSafariSessionDismissed();

    if (isIosSafariInstallEligible()) {
      if (!iosSessionDismissed && !dismissedForSession) {
        setManualKind('ios');
        requestInstallBanner();
      }
    } else if (!installedByFlag && isMacSafariInstallEligible()) {
      setManualKind('mac');
      requestInstallBanner();
    } else if (installedByFlag) {
      setWantsInstall(false);
    }

    const supportsBeforeInstallPrompt = 'onbeforeinstallprompt' in window;
    const shouldUseManualFallback = !supportsBeforeInstallPrompt || !window.isSecureContext;

    const fallbackTimer = window.setTimeout(() => {
      if (!shouldUseManualFallback) return;
      if (isRunningAsInstalledApp()) return;
      if (dismissedForSession) return;
      if (deferredPromptRef.current) return;
      if (localStorage.getItem(PWA_INSTALLED_KEY) === 'true') return;

      if (isIosSafariInstallEligible()) {
        if (isIosSafariSessionDismissed()) return;
        setManualKind('ios');
        requestInstallBanner();
        return;
      }

      if (isMacSafariInstallEligible()) {
        setManualKind('mac');
      } else {
        setManualKind('generic');
      }
      requestInstallBanner();
    }, PROMPT_FALLBACK_DELAY_MS);

    return () => {
      cleanupInstalledChecks();
      window.clearTimeout(fallbackTimer);
      unsubscribe();
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, [dismissedForSession, requestInstallBanner]);

  const dismissBanner = () => {
    if (isIosSafariBrowserTab()) {
      dismissIosSafariInstallForSession();
    }
    setDismissedForSession(true);
    setWantsInstall(false);
  };

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    setIsInstalling(true);
    try {
      await deferredPrompt.prompt();
      const choice = await deferredPrompt.userChoice;
      if (choice.outcome === 'accepted') {
        markPwaInstalled();
        setWantsInstall(false);
      }
    } finally {
      clearDeferredInstallPrompt();
      setDeferredPrompt(null);
      setIsInstalling(false);
    }
  };

  const showSecurityHint = shouldShowNonChromeInstallHint();

  const manualHelpText = useMemo(() => {
    if (manualKind === 'ios') return t('pwa.installHelpIos');
    if (manualKind === 'mac') return t('pwa.installHelpMac');
    if (manualKind === 'generic') {
      return window.isSecureContext ? t('pwa.installHelpGenericSecure') : t('pwa.installHelpGenericInsecure');
    }
    return t('pwa.installHelpDefault');
  }, [manualKind, t]);

  const securityHintText = t('pwa.installSecurityHint', {
    defaultValue:
      'This browser may show an Android security warning during install. You can confirm it to finish, or install from Chrome to avoid that warning.',
  });

  const shouldRender = useMemo(
    () =>
      wantsInstall &&
      !updateBannerVisible &&
      (!!deferredPrompt || manualKind !== null),
    [wantsInstall, updateBannerVisible, deferredPrompt, manualKind]
  );

  useEffect(() => {
    if (shouldRender) {
      setIsMounted(true);
      const frameId = window.requestAnimationFrame(() => {
        setIsAnimatingIn(true);
      });
      return () => window.cancelAnimationFrame(frameId);
    }

    setIsAnimatingIn(false);
    const timeoutId = window.setTimeout(() => {
      setIsMounted(false);
    }, ANIMATION_DURATION_MS);
    return () => window.clearTimeout(timeoutId);
  }, [shouldRender]);

  if (!isMounted) return null;

  const showNativeInstallButton = !!deferredPrompt;
  const showIosAlreadyInstalledButton = manualKind === 'ios';

  return (
    <div
      className={`fixed left-3 right-3 bottom-3 z-[10000] transform transition-all duration-300 ease-out sm:left-6 sm:right-6 sm:bottom-4 lg:left-1/2 lg:right-auto lg:w-1/2 lg:-translate-x-1/2 ${className} ${
        isAnimatingIn ? 'translate-y-0 opacity-100' : 'translate-y-6 opacity-0'
      }`}
    >
      <div className="relative overflow-hidden rounded-2xl border border-amber-200/60 bg-white/80 text-gray-900 shadow-2xl backdrop-blur-md dark:border-amber-800/50 dark:bg-black/80 dark:text-gray-100 p-4 sm:p-5">
        <span
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 bg-gradient-to-r from-slate-950/40 via-slate-800/18 to-transparent dark:hidden"
        />
        <div className="flex items-start justify-between gap-3 sm:gap-4">
          <div className="flex min-w-0 flex-1 items-start gap-3 sm:gap-3.5">
            <div className="relative h-20 w-20 shrink-0 sm:h-24 sm:w-24">
              <AppBrandLogo className="relative h-20 w-20 drop-shadow-[0_0_64px_rgba(15,23,42,1)] dark:drop-shadow-[0_0_14px_rgba(251,191,36,0.42)] sm:h-24 sm:w-24" alt="" />
            </div>
            <div className="min-w-0 pt-0.5">
              <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 sm:text-base">
                <span className="sm:hidden">{t('pwa.installShort')}</span>
                <span className="hidden sm:inline">{t('pwa.installTitle')}</span>
              </p>
              <p className="mt-1 text-xs text-gray-700 dark:text-gray-300 sm:text-sm">{manualHelpText}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={dismissBanner}
            className="px-2 py-1 text-gray-500 hover:text-gray-800 dark:text-gray-300 dark:hover:text-white"
            aria-label={t('pwa.closeInstallNotification')}
          >
            ✕
          </button>
        </div>
        {showSecurityHint && (
          <p className="mt-3 w-full rounded-lg border border-amber-300/80 bg-amber-50/90 px-2.5 py-1.5 text-[11px] leading-relaxed text-amber-950 dark:border-amber-700/70 dark:bg-amber-950/50 dark:text-amber-100 sm:text-xs">
            {securityHintText}
          </p>
        )}
        <div className="mt-3 flex flex-wrap items-center justify-center gap-2">
          <button
            type="button"
            onClick={dismissBanner}
            className="rounded-lg border border-gray-300/70 bg-white/40 px-3 py-2 text-sm text-gray-700 hover:bg-white/70 dark:border-gray-600 dark:bg-gray-800/60 dark:text-gray-200 dark:hover:bg-gray-800/80"
          >
            {t('pwa.dismiss')}
          </button>
          {showIosAlreadyInstalledButton && (
            <button
              type="button"
              onClick={dismissBanner}
              className="rounded-lg border border-gray-300/70 bg-white/40 px-3 py-2 text-sm text-gray-700 hover:bg-white/70 dark:border-gray-600 dark:bg-gray-800/60 dark:text-gray-200 dark:hover:bg-gray-800/80"
            >
              {t('pwa.alreadyOnHomeScreen')}
            </button>
          )}
          {showNativeInstallButton && (
            <button
              type="button"
              onClick={handleInstall}
              disabled={isInstalling}
              className="rounded-lg bg-amber-600 px-3 py-2 text-sm text-white hover:bg-amber-700 disabled:cursor-not-allowed disabled:opacity-70"
            >
              {isInstalling ? (
                t('pwa.opening')
              ) : (
                <>
                  <span className="sm:hidden">{t('pwa.addToHomeScreen')}</span>
                  <span className="hidden sm:inline">{t('pwa.addToDesktop')}</span>
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default PwaInstallBanner;
